import {useEffect, useState} from 'react'
import type {Vendor, VendorDeliveryRecord} from '../core/_models'
import {getVendorDeliveryHistory} from '../core/_requests'

interface Props { visible: boolean; vendor: Vendor | null; onClose: () => void }

const VendorPerformanceModal = ({visible, vendor, onClose}: Props) => {
  const [rows, setRows] = useState<VendorDeliveryRecord[]>([])
  const [page, setPage] = useState(0)
  const [last, setLast] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!visible || !vendor) return
    setRows([]); setPage(0); setLast(false); setError(null)
    setLoading(true)
    getVendorDeliveryHistory(vendor.id, 0, 5)
      .then((result) => { setRows(result.content || []); setLast(result.number + 1 >= result.totalPages) })
      .catch((e) => setError(e.message || 'Failed to load delivery history'))
      .finally(() => setLoading(false))
  }, [visible, vendor])

  if (!visible || !vendor) return null
  const categories = vendor.categoryPerformance || []
  const loadMore = async () => {
    if (loading || last) return
    setLoading(true)
    try {
      const next = page + 1
      const result = await getVendorDeliveryHistory(vendor.id, next, 5)
      setRows((current) => [...current, ...(result.content || [])])
      setPage(next); setLast(next + 1 >= result.totalPages)
    } catch (e: any) { setError(e.message || 'Failed to load delivery history') }
    finally { setLoading(false) }
  }

  return <div className='modal d-block' tabIndex={-1} role='dialog' style={{background: 'rgba(0,0,0,.45)'}}>
    <div className='modal-dialog modal-xl modal-dialog-scrollable' role='document'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Vendor performance — {vendor.name}</h5>
          <button type='button' className='btn-close' aria-label='Close' onClick={onClose} />
        </div>
        <div className='modal-body'>
          <div className='row g-3 mb-4'>
            <div className='col-md-3'><strong>Code</strong><div>{vendor.code || '-'}</div></div>
            <div className='col-md-3'><strong>Email</strong><div>{vendor.email || '-'}</div></div>
            <div className='col-md-2'><strong>Total orders</strong><div>{vendor.totalOrders ?? 0}</div></div>
            <div className='col-md-2'><strong>Delivery records</strong><div>{vendor.deliveredRecords ?? 0}</div></div>
            <div className='col-md-2'><strong>Rating</strong><div>{vendor.performanceRating == null ? 'Not rated' : vendor.performanceRating.toFixed(2)}</div></div>
          </div>
          <h6 className='fw-bold'>Category performance</h6>
          {categories.length === 0 ? <div className='text-muted mb-4'>No delivery history</div> : <div className='table-responsive mb-4'>
            <table className='table table-sm table-bordered'><thead><tr><th>Category</th><th>Orders</th><th>Records</th><th>Avg days</th><th>Rating</th><th>Issues</th></tr></thead>
              <tbody>{categories.map((c) => <tr key={`${c.categoryId ?? 'none'}-${c.categoryName}`}><td>{c.categoryName}</td><td>{c.totalCompletedOrders}</td><td>{c.deliveredRecords}</td><td>{c.averageDeliveryDays == null ? '-' : c.averageDeliveryDays.toFixed(1)}</td><td>{c.rating == null ? 'Not rated' : c.rating.toFixed(2)}</td><td>{c.issueItemCount}</td></tr>)}</tbody>
            </table>
          </div>}
          <h6 className='fw-bold'>Delivery history</h6>
          {error && <div className='alert alert-danger'>{error}</div>}
          <div className='table-responsive'><table className='table table-sm table-bordered align-middle'><thead><tr><th>GR</th><th>PO</th><th>Category</th><th>Vessel</th><th>Received</th><th>Days</th><th>Good</th><th>Damaged</th><th>Missing</th><th>Rating</th></tr></thead>
            <tbody>{rows.length === 0 && !loading ? <tr><td colSpan={10} className='text-center text-muted'>No delivery history</td></tr> : rows.map((row) => <tr key={row.deliveryId}><td>{row.deliveryId}</td><td>{row.purchaseOrderNumber || row.purchaseOrderId}</td><td>{row.categoryName}</td><td>{row.vesselName || '-'}</td><td>{row.receivedDate || '-'}</td><td>{row.deliveryDays ?? '-'}</td><td>{row.goodItemCount}</td><td>{row.damagedItemCount}</td><td>{row.missingItemCount}</td><td>{row.rating == null ? 'Not rated' : row.rating.toFixed(2)}</td></tr>)}</tbody>
          </table></div>
          <div className='text-center'>{loading && <span className='text-muted'>Loading…</span>}{!loading && !last && rows.length > 0 && <button className='btn btn-sm btn-light-primary' onClick={loadMore}>Load more</button>}</div>
        </div>
        <div className='modal-footer'><button type='button' className='btn btn-light' onClick={onClose}>Close</button></div>
      </div>
    </div>
  </div>
}

export default VendorPerformanceModal
